/**
 * TextBlastTab — /phone v2 Text Blast surface, iter 508j rebuild.
 *
 * Fixes that landed in this rewrite (Dawn 2026-02-25):
 *   • Backend picker repointed at the unified contact registry
 *     (/api/broadcasts/recipients). Previously ~45 clients; now up to
 *     ~146 total across client/caregiver/staff/provider/family/clinic.
 *   • Type filter chips at the top of the audience picker so a blast
 *     to just caregivers or just family contacts is one click, not a
 *     search-and-tick exercise.
 *   • Vertical 3-step wizard (Audience → Message → Review & Send).
 *     Each step gets the full width — no cramped 2-column picker +
 *     composer split, no truncated names, no tooltip-over-message
 *     z-index bug.
 *   • Sticky top strip shows the running recipient tally so you always
 *     know what you're about to send to.
 *   • Recent Blasts panel wired through /api/broadcasts/history (was
 *     hardcoded to "endpoint unavailable").
 *   • The old nested "New Broadcast / History / Audiences" tab bar is
 *     gone — those are now peer subtabs at the top of Text Blast.
 *   • Subtab state persists in the URL (?subtab=compose|history|
 *     audiences) so /phone?tab=text-blast&subtab=history deep-links
 *     to the history view. Follows the "state lives in the URL" rule
 *     the rest of Communications2 already enforces.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, MessageSquare, Send, Search, Check, ChevronRight, Loader2,
  Info, ShieldCheck, Clock, DollarSign, ArrowLeft, History, ListPlus,
  FileText, Plus,
} from "lucide-react";
import { NAVY, GOLD, GREEN, NAVY_HAIRLINE } from "./tokens";
import { smsTemplatesAPI } from "../../services/api";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Scope chip taxonomy — matches _SCOPE_TO_CONTACT_TYPES in
// backend/routers/broadcasts.py so the frontend and API agree on
// "who counts as a caregiver".
const SCOPES = [
  { k: "client",    label: "Clients",    hint: "Active patients" },
  { k: "caregiver", label: "Caregivers", hint: "PCWs + team" },
  { k: "staff",     label: "Staff",      hint: "Office team" },
  { k: "family",    label: "Family",     hint: "Guardians + emergency" },
  { k: "provider",  label: "Providers",  hint: "MDs · NPs · PTs" },
  { k: "clinic",    label: "Clinics",    hint: "Referring sites" },
  { k: "all",       label: "All",        hint: "Everyone with a phone" },
];

// Cost-per-segment estimate (US Twilio A2P 10DLC, one segment).
const COST_PER_SEG = 0.0079;

function formatPhone(p) {
  const d = String(p || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith("1")) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return p || "";
}

function segCount(body) {
  const len = (body || "").length;
  if (len === 0) return 0;
  if (len <= 160) return 1;
  return Math.ceil(len / 153);
}

/* ────────────────────────────────────────────────────────────
   Sub-tabs (Compose / History / Saved audiences)
   ──────────────────────────────────────────────────────────── */
function SubTabs({ active, onChange }) {
  const tabs = [
    { k: "compose",   label: "Compose",  icon: MessageSquare },
    { k: "history",   label: "History",  icon: History },
    { k: "audiences", label: "Saved audiences", icon: ListPlus },
  ];
  return (
    <div className="flex items-center gap-1 px-4 py-2 border-b bg-white shrink-0" style={{ borderColor: NAVY_HAIRLINE }}>
      {tabs.map(t => {
        const Icon = t.icon;
        const isActive = t.k === active;
        return (
          <button
            key={t.k}
            onClick={() => onChange(t.k)}
            className="h-8 px-3 rounded-md text-[12px] font-semibold flex items-center gap-1.5 transition"
            style={{
              background: isActive ? NAVY : "transparent",
              color: isActive ? "white" : NAVY,
            }}
            data-testid={`textblast-subtab-${t.k}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   STEP 1 · Audience picker
   ──────────────────────────────────────────────────────────── */
function AudienceStep({ scope, setScope, picked, setPicked, rowsCache, onContinue }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr("");
    (async () => {
      try {
        const url = `${API}/api/broadcasts/recipients?scope=${scope}&limit=2000`;
        const res = await fetch(url, { headers: authHdr() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (alive) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (alive) { setRows([]); setErr(e.message || "Failed to load"); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [scope]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const t = q.trim().toLowerCase();
    return rows.filter(r => (
      (`${r.first_name || ""} ${r.last_name || ""}`).toLowerCase().includes(t)
      || (r.phone || r.phone_primary || "").replace(/\D/g, "").includes(t.replace(/\D/g, ""))
      || (r.practice_name || r.company || "").toLowerCase().includes(t)
    ));
  }, [rows, q]);

  const smsable = useMemo(() => filtered.filter(r => r.smsable), [filtered]);
  const allSmsableSelected = smsable.length > 0 && smsable.every(r => picked.has(r.id));

  const toggle = (id) => setPicked(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const toggleAllSmsable = () => setPicked(prev => {
    const n = new Set(prev);
    if (allSmsableSelected) smsable.forEach(r => n.delete(r.id));
    else smsable.forEach(r => n.add(r.id));
    return n;
  });

  return (
    <div className="flex flex-col gap-3" data-testid="textblast-step-audience">
      {/* Header row: scope chips + Load audience menu */}
      <div className="flex items-center gap-2 flex-wrap">
        {SCOPES.map(s => (
          <button
            key={s.k}
            onClick={() => setScope(s.k)}
            className="h-8 px-3 rounded-full text-[12px] font-semibold border transition"
            style={scope === s.k
              ? { background: NAVY, color: "white", borderColor: NAVY }
              : { color: NAVY, background: "white", borderColor: NAVY_HAIRLINE }}
            title={s.hint}
            data-testid={`textblast-scope-${s.k}`}
          >
            {s.label}
          </button>
        ))}
        <div className="ml-auto">
          <LoadAudienceMenu onLoad={(a) => {
            // Load audience: pre-check every id from the audience so
            // the picker jumps straight to a fully-selected state. We
            // union with any current selection, then let the user
            // deselect if they want a subset.
            const ids = [
              ...((a.contact_ids || [])),
              ...((a.patient_ids || [])),
            ];
            setPicked(prev => {
              const n = new Set(prev);
              ids.forEach(id => n.add(id));
              return n;
            });
            toast.success(`Loaded audience "${a.name}" (${ids.length})`);
          }} />
        </div>
      </div>

      {/* Search + select-all */}
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 rounded-md border h-9 px-3 bg-white" style={{ borderColor: NAVY_HAIRLINE }}>
          <Search className="w-3.5 h-3.5 opacity-60" style={{ color: NAVY }} />
          <input
            className="flex-1 outline-none text-[13px]"
            placeholder="Search name, number, agency..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="textblast-audience-search"
          />
        </div>
        <button
          onClick={toggleAllSmsable}
          disabled={smsable.length === 0}
          className="h-9 px-3 rounded-md border text-[12px] font-semibold disabled:opacity-50"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY, background: "white" }}
          data-testid="textblast-select-all"
        >
          {allSmsableSelected
            ? `Deselect ${smsable.length}`
            : `Select all SMS-able (${smsable.length})`}
        </button>
      </div>

      {/* Recipient list */}
      <div className="rounded-lg border bg-white overflow-hidden" style={{ borderColor: NAVY_HAIRLINE }}>
        {loading && (
          <div className="p-6 text-center text-[12px] text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading recipients…
          </div>
        )}
        {err && !loading && (
          <div className="p-6 text-center text-[12px] text-red-600">{err}</div>
        )}
        {!loading && !err && filtered.length === 0 && (
          <div className="p-6 text-center text-[12px] text-slate-500">
            No contacts matched. Try a different scope or clear your search.
          </div>
        )}
        {!loading && !err && filtered.length > 0 && (
          <div className="max-h-[480px] overflow-y-auto">
            {filtered.map((r, i) => {
              const disabled = !r.smsable;
              const isPicked = picked.has(r.id);
              const displayName = `${r.first_name || ""} ${r.last_name || ""}`.trim()
                || r.practice_name
                || r.company
                || formatPhone(r.phone || r.phone_primary);
              return (
                <label
                  key={r.id}
                  className="flex items-center gap-3 px-4 py-2 border-b last:border-b-0 hover:bg-slate-50 transition"
                  style={{
                    borderColor: NAVY_HAIRLINE,
                    background: isPicked ? "#F0F9FF" : "white",
                    opacity: disabled ? 0.55 : 1,
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                  data-testid={`textblast-recipient-${i}`}
                >
                  <input
                    type="checkbox"
                    checked={isPicked}
                    disabled={disabled}
                    onChange={() => !disabled && toggle(r.id)}
                    className="w-4 h-4"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate" style={{ color: NAVY }}>
                      {displayName || <span className="italic text-slate-400">Unnamed</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono flex items-center gap-2">
                      {formatPhone(r.phone || r.phone_primary)}
                      {r.contact_type && (
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">
                          · {r.contact_type}
                        </span>
                      )}
                      {r.skip_reason && (
                        <span className="text-[10.5px] text-amber-600">· {r.skip_reason}</span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <SaveAsAudiencePopover
          pickedRows={(rowsCache || []).filter(r => picked.has(r.id))}
        />
        <button
          disabled={picked.size === 0}
          onClick={onContinue}
          className="h-10 px-5 rounded-lg text-[13px] font-bold flex items-center gap-2 hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: GOLD, color: NAVY }}
          data-testid="textblast-continue-to-message"
        >
          Continue to message
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   STEP 2 · Message composer
   ──────────────────────────────────────────────────────────── */
function MessageStep({ template, setTemplate, name, setName, sampleRecipient, onBack, onContinue }) {
  const preview = useMemo(() => {
    let out = template || "";
    if (!sampleRecipient) return out;
    const first = sampleRecipient.first_name || "there";
    const last = sampleRecipient.last_name || "";
    out = out.replace(/\{first_name\}/g, first)
             .replace(/\{last_name\}/g, last)
             .replace(/\{agency\}/g, "Avida Personal Care");
    return out;
  }, [template, sampleRecipient]);
  const segs = segCount(template);

  // ── Saved SMS templates (canned messages) ──────────────────────────
  const [tpls, setTpls] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  useEffect(() => {
    smsTemplatesAPI.list().then((r) => setTpls(r.data?.templates || [])).catch(() => {});
  }, []);
  const applyTemplate = (t) => {
    setTemplate(t.body || "");
    if (!name.trim()) setName(t.title || "");
    setTplOpen(false);
    toast.success(`Loaded "${t.title}"`);
  };
  const saveAsTemplate = async () => {
    const body = (template || "").trim();
    if (!body) { toast.error("Write a message first"); return; }
    const title = (name || "").trim() || body.slice(0, 40);
    setSavingTpl(true);
    try {
      const r = await smsTemplatesAPI.create({ title, body, category: "Custom" });
      setTpls((prev) => [...prev, r.data].sort((a, b) => (a.category || "").localeCompare(b.category || "") || (a.title || "").localeCompare(b.title || "")));
      toast.success("Saved as template");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Only admins can save templates" : (e?.response?.data?.detail || "Could not save template"));
    } finally { setSavingTpl(false); }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="textblast-step-message">
      <div className="flex flex-col gap-1">
        <label className="text-[11px] font-black uppercase tracking-wider" style={{ color: NAVY }}>
          Broadcast name (internal, for your history)
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Office closed Friday"
          className="h-10 px-3 rounded-md border text-[13px] outline-none"
          style={{ borderColor: NAVY_HAIRLINE }}
          data-testid="textblast-name"
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="relative">
          <button
            onClick={() => setTplOpen((v) => !v)}
            className="h-8 px-3 rounded-md border text-[12px] font-bold flex items-center gap-1.5 hover:bg-slate-50"
            style={{ borderColor: NAVY_HAIRLINE, color: NAVY, background: "white" }}
            data-testid="textblast-template-open"
          >
            <FileText className="w-3.5 h-3.5" /> Use a template
            {tpls.length > 0 && <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full" style={{ background: "rgba(3,40,102,0.06)" }}>{tpls.length}</span>}
          </button>
          {tplOpen && (
            <>
              <button className="fixed inset-0 z-10 cursor-default" onClick={() => setTplOpen(false)} aria-label="Close templates" data-testid="textblast-template-backdrop" />
              <div className="absolute z-20 mt-1 w-[380px] max-h-72 overflow-y-auto rounded-lg border bg-white shadow-xl" style={{ borderColor: NAVY_HAIRLINE }} data-testid="textblast-template-list">
                {tpls.length === 0 ? (
                  <div className="p-4 text-[12px] text-slate-400 text-center">No templates yet. Write a message and tap "Save as template".</div>
                ) : tpls.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    data-testid={`textblast-template-${t.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0"
                    style={{ borderColor: NAVY_HAIRLINE }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[12.5px] font-bold" style={{ color: NAVY }}>{t.title}</span>
                      <span className="ml-auto text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "rgba(3,40,102,0.06)", color: NAVY }}>{t.category}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">{t.body}</div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <button
          onClick={saveAsTemplate}
          disabled={savingTpl || !template.trim()}
          className="h-8 px-3 rounded-md border text-[12px] font-bold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY, background: "white" }}
          data-testid="textblast-template-save"
        >
          {savingTpl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Save as template
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-semibold text-slate-500">Merge tokens:</span>
        {["{first_name}", "{last_name}", "{agency}"].map(tok => (
          <button
            key={tok}
            onClick={() => setTemplate((template || "") + tok)}
            className="h-6 px-2 rounded-full text-[11px] font-mono border hover:bg-slate-50"
            style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          >
            + {tok}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-[11px] font-black uppercase tracking-wider" style={{ color: NAVY }}>
          Message body
        </label>
        <textarea
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          rows={7}
          placeholder="Hi {first_name}, this is Avida Personal Care..."
          className="w-full px-3 py-2 rounded-md border text-[13px] font-mono outline-none resize-y"
          style={{ borderColor: NAVY_HAIRLINE }}
          data-testid="textblast-template"
        />
        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span>{(template || "").length} chars · {segs} SMS segment{segs === 1 ? "" : "s"}</span>
          <span className="text-slate-400 italic">STOP handling is added automatically to the first blast per recipient.</span>
        </div>
      </div>

      {sampleRecipient && (
        <div className="rounded-lg border p-3" style={{ background: "#F8FAFC", borderColor: NAVY_HAIRLINE }} data-testid="textblast-preview">
          <div className="text-[10.5px] font-black uppercase tracking-wider mb-1" style={{ color: NAVY }}>
            Preview for {sampleRecipient.first_name || "recipient"} {sampleRecipient.last_name || ""}
          </div>
          <div className="text-[13px] whitespace-pre-wrap font-mono">
            {preview || <span className="italic text-slate-400">Nothing to preview yet — type a message above.</span>}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="h-10 px-4 rounded-lg border text-[13px] font-semibold flex items-center gap-1.5"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY, background: "white" }}
          data-testid="textblast-back-to-audience"
        >
          <ArrowLeft className="w-4 h-4" /> Change audience
        </button>
        <button
          disabled={!template.trim() || !name.trim()}
          onClick={onContinue}
          className="h-10 px-5 rounded-lg text-[13px] font-bold flex items-center gap-2 hover:brightness-95 disabled:opacity-50"
          style={{ background: GOLD, color: NAVY }}
          data-testid="textblast-continue-to-review"
        >
          Continue to review
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   STEP 3 · Review & send
   ──────────────────────────────────────────────────────────── */
function ReviewStep({ template, pickedRows, onBack, onSend, sending }) {
  const segs = segCount(template);
  const cost = (segs * pickedRows.length * COST_PER_SEG).toFixed(2);
  return (
    <div className="flex flex-col gap-3" data-testid="textblast-step-review">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border p-3 bg-white" style={{ borderColor: NAVY_HAIRLINE }}>
          <div className="text-[10.5px] font-black uppercase tracking-wider mb-1" style={{ color: NAVY }}>
            Recipients
          </div>
          <div className="text-[24px] font-black" style={{ color: NAVY }}>
            {pickedRows.length}
          </div>
          <div className="text-[11px] text-slate-500">All flagged SMS-able · STOP filter applied</div>
        </div>
        <div className="rounded-lg border p-3 bg-white" style={{ borderColor: NAVY_HAIRLINE }}>
          <div className="text-[10.5px] font-black uppercase tracking-wider mb-1" style={{ color: NAVY }}>
            Estimated cost
          </div>
          <div className="text-[24px] font-black" style={{ color: NAVY }}>
            <DollarSign className="inline w-5 h-5 mb-1" />{cost}
          </div>
          <div className="text-[11px] text-slate-500">
            {pickedRows.length} × {segs} segment{segs === 1 ? "" : "s"} @ ${COST_PER_SEG}/seg
          </div>
        </div>
      </div>

      <div className="rounded-lg border p-3 bg-white" style={{ borderColor: NAVY_HAIRLINE }}>
        <div className="text-[10.5px] font-black uppercase tracking-wider mb-1" style={{ color: NAVY }}>
          Message
        </div>
        <div className="text-[13px] font-mono whitespace-pre-wrap">{template}</div>
      </div>

      <div className="rounded-lg border p-3" style={{ background: "#F0F9FF", borderColor: "#BAE6FD" }} data-testid="textblast-compliance">
        <div className="text-[10.5px] font-black uppercase tracking-wider mb-2" style={{ color: "#075985" }}>
          Before sending
        </div>
        <ul className="text-[12px] text-slate-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-600" />
            STOP-handling automatically appended to recipients&apos; first blast.
          </li>
          <li className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 mt-0.5 text-blue-600" />
            Message will be scanned for PHI keywords before delivery.
          </li>
          <li className="flex items-start gap-2">
            <Clock className="w-3.5 h-3.5 mt-0.5 text-amber-600" />
            Quiet hours (8pm-8am local) enforced by the scheduler.
          </li>
        </ul>
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="h-10 px-4 rounded-lg border text-[13px] font-semibold flex items-center gap-1.5"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY, background: "white" }}
          data-testid="textblast-back-to-message"
        >
          <ArrowLeft className="w-4 h-4" /> Edit message
        </button>
        <button
          onClick={onSend}
          disabled={sending || pickedRows.length === 0}
          className="h-11 px-5 rounded-lg text-white text-[14px] font-bold flex items-center gap-2 hover:brightness-95 disabled:opacity-50"
          style={{ background: GREEN }}
          data-testid="textblast-send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send blast to {pickedRows.length}
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   History subtab
   ──────────────────────────────────────────────────────────── */
function HistorySubtab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/broadcasts/history?limit=50`, { headers: authHdr() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (alive) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (alive) setErr(e.message || "Failed to load history");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="p-6 text-center text-[13px] text-slate-500 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
      </div>
    );
  }
  if (err) return <div className="p-6 text-center text-[12px] text-red-600">{err}</div>;
  if (rows.length === 0) {
    return (
      <div className="p-8 text-center" data-testid="textblast-history-empty">
        <History className="w-8 h-8 mx-auto text-slate-300 mb-2" />
        <div className="text-[14px] font-medium" style={{ color: NAVY }}>No blasts sent yet</div>
        <div className="text-[12px] text-slate-500 mt-1">Sent blasts will show here with delivery stats.</div>
      </div>
    );
  }
  return (
    <div className="p-4 space-y-2" data-testid="textblast-history">
      {rows.map(r => (
        <div key={r.id} className="rounded-lg border p-3 bg-white flex items-center gap-3" style={{ borderColor: NAVY_HAIRLINE }}>
          <MessageSquare className="w-5 h-5 text-slate-400" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold truncate" style={{ color: NAVY }}>{r.name}</div>
            <div className="text-[11px] text-slate-500 truncate">{r.template}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[13px] font-black" style={{ color: NAVY }}>
              {(r.patient_ids || []).length + (r.contact_ids || []).length}
            </div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-400">recipients</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function StepDot({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-1">
      <span
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold"
        style={{
          background: done ? GREEN : active ? NAVY : "#E2E8F0",
          color: done || active ? "white" : "#64748B",
        }}
      >
        {done ? <Check className="w-2.5 h-2.5" /> : n}
      </span>
      <span style={{ color: active ? NAVY : "#94A3B8" }}>{label}</span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Saved Audiences subtab — iter 508j (Dawn 2026-02-25)
   ────────────────────────────────────────────────────────────
   Lists saved audiences, deletes them, and shows how many recipients
   each holds. Creation happens inside the Compose flow's Audience
   step (via SaveAsAudiencePopover below) so the "save what I'm
   looking at" gesture stays one click.
*/
function AudiencesSubtab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/broadcasts/audiences`, { headers: authHdr() });
      if (res.ok) setRows(await res.json());
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this saved audience?")) return;
    setBusy(b => ({ ...b, [id]: true }));
    try {
      const res = await fetch(`${API}/api/broadcasts/audiences/${id}`, {
        method: "DELETE", headers: authHdr(),
      });
      if (res.ok) {
        setRows(prev => prev.filter(r => r.id !== id));
        toast.success("Audience deleted");
      } else {
        toast.error("Delete failed");
      }
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
    setBusy(b => ({ ...b, [id]: false }));
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-[13px] text-slate-500 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading audiences…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="p-8 text-center" data-testid="textblast-audiences-empty">
        <ListPlus className="w-8 h-8 mx-auto text-slate-300 mb-2" />
        <div className="text-[14px] font-medium" style={{ color: NAVY }}>No saved audiences yet</div>
        <div className="text-[12px] text-slate-500 mt-1 max-w-md mx-auto">
          Pick recipients in the Compose step, then tap
          &ldquo;Save as audience&rdquo; to keep them one tap away.
        </div>
      </div>
    );
  }
  return (
    <div className="p-4 space-y-2" data-testid="textblast-audiences">
      {rows.map(a => {
        const total = (a.patient_ids || []).length + (a.contact_ids || []).length;
        return (
          <div
            key={a.id}
            className="rounded-lg border p-3 bg-white flex items-center gap-3"
            style={{ borderColor: NAVY_HAIRLINE }}
            data-testid={`textblast-audience-row-${a.id}`}
          >
            <Users className="w-5 h-5 text-slate-400" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold truncate" style={{ color: NAVY }}>{a.name}</div>
              {a.description && (
                <div className="text-[11px] text-slate-500 truncate">{a.description}</div>
              )}
              <div className="text-[10.5px] text-slate-400 mt-0.5">
                Created by {a.created_by_name || "—"} · {(a.created_at || "").slice(0, 10)}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[13px] font-black" style={{ color: NAVY }}>{total}</div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-400">recipients</div>
            </div>
            <button
              onClick={() => remove(a.id)}
              disabled={busy[a.id]}
              className="h-8 px-3 rounded-md border text-[11.5px] font-semibold hover:bg-slate-50 disabled:opacity-50"
              style={{ borderColor: "#FECACA", color: "#B91C1C" }}
              data-testid={`textblast-audience-delete-${a.id}`}
            >
              Delete
            </button>
          </div>
        );
      })}
    </div>
  );
}


/* Save-as-audience popover — appears next to the audience picker's
   "Continue" button when at least one recipient is selected. */
function SaveAsAudiencePopover({ pickedRows, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || pickedRows.length === 0) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/broadcasts/audiences`, {
        method: "POST",
        headers: { ...authHdr(), "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          contact_ids: pickedRows.map(r => r.id),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Saved audience "${name.trim()}"`);
      setName("");
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    }
    setSaving(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        disabled={pickedRows.length === 0}
        className="h-10 px-4 rounded-lg border text-[12.5px] font-semibold hover:bg-white disabled:opacity-40"
        style={{ borderColor: NAVY_HAIRLINE, color: NAVY, background: "transparent" }}
        data-testid="textblast-save-as-audience"
      >
        <ListPlus className="w-3.5 h-3.5 inline mr-1.5" />
        Save as audience
      </button>
    );
  }

  return (
    <div
      className="rounded-lg border p-3 bg-white flex items-center gap-2 shadow-sm"
      style={{ borderColor: NAVY_HAIRLINE }}
      data-testid="textblast-save-as-audience-form"
    >
      <input
        autoFocus
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") { setOpen(false); setName(""); } }}
        placeholder="Name this audience (e.g. Friday PCWs)"
        className="h-9 px-2 rounded-md border text-[12.5px] outline-none min-w-[240px]"
        style={{ borderColor: NAVY_HAIRLINE }}
      />
      <button
        onClick={save}
        disabled={!name.trim() || saving}
        className="h-9 px-3 rounded-md text-white text-[12px] font-semibold disabled:opacity-50"
        style={{ background: NAVY }}
        data-testid="textblast-save-as-audience-confirm"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
      </button>
      <button
        onClick={() => { setOpen(false); setName(""); }}
        className="h-9 px-2 text-[12px] text-slate-500 hover:text-slate-900"
      >
        Cancel
      </button>
    </div>
  );
}


/* Load audience dropdown — top of Audience step. */
function LoadAudienceMenu({ onLoad }) {
  const [audiences, setAudiences] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/broadcasts/audiences`, { headers: authHdr() });
        if (res.ok) setAudiences(await res.json());
      } catch { /* silent */ }
    })();
  }, []);

  if (audiences.length === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="h-8 px-3 rounded-md border text-[12px] font-semibold hover:bg-white"
        style={{ borderColor: NAVY_HAIRLINE, color: NAVY, background: "transparent" }}
        data-testid="textblast-load-audience"
      >
        <History className="w-3.5 h-3.5 inline mr-1.5" />
        Load audience ({audiences.length})
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 rounded-lg border bg-white shadow-lg z-20 min-w-[280px] max-h-[320px] overflow-y-auto"
          style={{ borderColor: NAVY_HAIRLINE }}
          data-testid="textblast-load-audience-menu"
        >
          {audiences.map(a => {
            const n = (a.patient_ids || []).length + (a.contact_ids || []).length;
            return (
              <button
                key={a.id}
                onClick={() => { onLoad(a); setOpen(false); }}
                className="w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-slate-50"
                style={{ borderColor: NAVY_HAIRLINE }}
                data-testid={`textblast-load-audience-${a.id}`}
              >
                <div className="text-[12.5px] font-semibold" style={{ color: NAVY }}>{a.name}</div>
                <div className="text-[11px] text-slate-500">{n} recipients</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Wrapper
   ──────────────────────────────────────────────────────────── */
export default function TextBlastTab() {
  // Iter 508j — subtab persists in ?subtab= so deep-links and reloads
  // land on the requested view (previously local useState only).
  const [params, setParams] = useSearchParams();
  const subtab = ["compose", "history", "audiences"].includes(params.get("subtab"))
    ? params.get("subtab") : "compose";
  const setSubtab = (t) => setParams(p => {
    const n = new URLSearchParams(p);
    if (t === "compose") n.delete("subtab");
    else n.set("subtab", t);
    return n;
  }, { replace: true });
  const [step, setStep] = useState(1);
  const [scope, setScope] = useState("client");
  const [picked, setPicked] = useState(new Set());
  const [rowsCache, setRowsCache] = useState([]);
  const [template, setTemplate] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);

  // Cache rows across step nav so ReviewStep + preview have data.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/broadcasts/recipients?scope=all&limit=3000`, { headers: authHdr() });
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setRowsCache(Array.isArray(data) ? data : []);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  const pickedRows = useMemo(
    () => rowsCache.filter(r => picked.has(r.id)),
    [rowsCache, picked],
  );
  const sampleRecipient = pickedRows[0] || null;

  const send = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/api/broadcasts/send`, {
        method: "POST",
        headers: { ...authHdr(), "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          template: template.trim(),
          contact_ids: pickedRows.map(r => r.id),
          confirm_phi_warnings: true,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success(`Blast queued for ${pickedRows.length} recipient${pickedRows.length === 1 ? "" : "s"}`);
      setPicked(new Set());
      setTemplate("");
      setName("");
      setStep(1);
      setSubtab("history");
    } catch (e) {
      toast.error(`Send failed: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-slate-50" data-testid="textblast-tab">
      <SubTabs active={subtab} onChange={setSubtab} />

      {subtab === "compose" && (
        <>
          {/* Sticky selection summary — visible on every step so you
              never lose sight of the audience size while composing. */}
          <div className="px-4 py-2 border-b bg-white shrink-0 flex items-center gap-3" style={{ borderColor: NAVY_HAIRLINE }}>
            <Users className="w-4 h-4" style={{ color: NAVY }} />
            <div className="text-[13px] font-semibold" style={{ color: NAVY }} data-testid="textblast-selection-count">
              {picked.size} recipient{picked.size === 1 ? "" : "s"} selected
            </div>
            <div className="flex items-center gap-1 text-[11px] text-slate-500">
              <StepDot n={1} label="Audience" active={step === 1} done={step > 1} />
              <ChevronRight className="w-3 h-3 text-slate-300" />
              <StepDot n={2} label="Message" active={step === 2} done={step > 2} />
              <ChevronRight className="w-3 h-3 text-slate-300" />
              <StepDot n={3} label="Review" active={step === 3} done={false} />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="max-w-4xl mx-auto">
              {step === 1 && (
                <AudienceStep
                  scope={scope}
                  setScope={setScope}
                  picked={picked}
                  setPicked={setPicked}
                  rowsCache={rowsCache}
                  onContinue={() => setStep(2)}
                />
              )}
              {step === 2 && (
                <MessageStep
                  template={template}
                  setTemplate={setTemplate}
                  name={name}
                  setName={setName}
                  sampleRecipient={sampleRecipient}
                  onBack={() => setStep(1)}
                  onContinue={() => setStep(3)}
                />
              )}
              {step === 3 && (
                <ReviewStep
                  template={template}
                  pickedRows={pickedRows}
                  onBack={() => setStep(2)}
                  onSend={send}
                  sending={sending}
                />
              )}
            </div>
          </div>
        </>
      )}

      {subtab === "history" && <HistorySubtab />}

      {subtab === "audiences" && <AudiencesSubtab />}
    </div>
  );
}
